import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaPaperPlane, FaSpinner, FaExclamationTriangle, FaRedo, FaInfoCircle, 
  FaExternalLinkAlt, FaClock, FaClipboardCheck, FaCalculator, FaVideo, 
  FaPlay, FaArrowRight, FaLock, FaExclamationCircle
} from 'react-icons/fa';

// Import simplified components
import SimplifiedTimeline from './SimplifiedTimeline';
import SimplifiedChecklist from './SimplifiedChecklist';
import SimplifiedCalculator from './SimplifiedCalculator';
import AuthForm from './AuthForm';

// Import content mapping utilities
import { detectTopics, getRelevantContent } from '../../utils/contentMapping';

const resourcePathMap = {
    // Guides
    '/guides/divorce': '/guides/divorce',
    '/guides/garde': '/guides/garde',
    '/guides/pension': '/guides/pension',
    '/guides/separation': '/guides/separation',
    // Timelines
    '/outils/chronologie-divorce': '/timeline/divorce',
    '/outils/chronologie-garde': '/timeline/garde',
    '/outils/chronologie-pension': '/timeline/pension',
    '/outils/chronologie-separation': '/timeline/separation',
    // Checklists
    '/outils/checklist-divorce': '/checklists/divorce',
    '/outils/checklist-garde': '/checklists/garde',
    '/outils/checklist-pension': '/checklists/pension',
    '/outils/checklist-separation': '/checklists/separation',
    // Calculators
    '/outils/calculateur-pension': '/calculators/pension',
    '/outils/calculateur-divorce': '/calculators/divorce',
    '/outils/calculateur-garde': '/calculators/garde',
    '/outils/calculateur-separation': '/calculators/separation',
    '/outils/calculateur-pension-ex-epoux': '/calculators/divorce-pension',
    '/calculators': '/calculators', 
    '/delay-calculator': '/delays', 
    // Orientation
    '/resources/aide-juridictionnelle': '/orientation/aide-juridique',
    '/resources/trouver-avocat': '/orientation/cabinets',
    '/resources/orientation': '/orientation/ressources',
    // Other
    '/confidentiality': '/confidentiality',
    '/testimonials': '/testimonials',
};

// Resource descriptions for providing more context to users
const resourceDescriptions = {
    '/guides/divorce': "Guide complet du divorce avec toutes les étapes et procédures au Québec. Plus de détails en fonction de votre situation personnelle.",
    '/timeline/divorce': "Les délais de divorce varient considérablement selon votre situation personnelle. Des facteurs comme la présence d'enfants, le partage des biens, les projets de voyage, les différences religieuses, etc. peuvent allonger la procédure. Notre outil peut vous donner une estimation générale, mais n'oubliez pas: aucune signature de votre conjoint(e) n'est requise pour introduire une procédure de divorce.",
    '/checklists/divorce': "Liste détaillée des documents et démarches nécessaires pour votre procédure de divorce, adaptable selon votre situation personnelle.",
    '/calculators/divorce': "Estimez les coûts possibles de votre divorce selon votre situation personnelle et vos besoins spécifiques.",
    '/delays': "Calculateur de délais pour comprendre la durée estimée de votre procédure. Les délais varient selon plusieurs facteurs que notre outil prend en compte.",
    '/timeline/garde': "Chronologie des étapes pour établir un arrangement de garde d'enfants, avec des détails supplémentaires basés sur votre situation.",
    '/timeline/pension': "Calendrier des procédures pour établir ou modifier une pension alimentaire, adapté à votre cas particulier."
};

// Update the resourcePathMap with descriptions
Object.entries(resourcePathMap).forEach(([key, value]) => {
  if (resourceDescriptions[value]) {
    resourcePathMap[key] = { path: value, description: resourceDescriptions[value] };
  }
});

// Basic resources that don't require API calls
const BASIC_RESOURCES = {
  divorce: [
    { title: "Guide complet du divorce au Québec", url: "/guides/divorce" },
    { title: "Chronologie du divorce", url: "/timeline/divorce" },
    { title: "Checklist divorce", url: "/checklist/divorce" },
    { title: "Calculateur de pension alimentaire", url: "/calculators/pension" },
  ],
  separation: [
    { title: "Guide de la séparation légale", url: "/guides/separation" },
    { title: "Chronologie de la séparation", url: "/timeline/separation" }, 
    { title: "Checklist de séparation", url: "/checklist/separation" },
  ],
  custody: [
    { title: "Guide complet sur la garde d'enfants", url: "/guides/garde" },
    { title: "Chronologie de la garde d'enfants", url: "/timeline/garde" },
    { title: "Checklist de garde d'enfants", url: "/checklist/garde" },
  ],
  child_support: [
    { title: "Guide de la pension alimentaire", url: "/guides/pension" },
    { title: "Calculateur de pension alimentaire", url: "/calculators/pension" },
  ],
  legal_aid: [
    { title: "Aide juridique - Critères d'admissibilité", url: "https://www.csj.qc.ca/commission-des-services-juridiques/aide-juridique/admissibilite/fr", external: true },
    { title: "Consultations juridiques gratuites", url: "https://jurisreference.ca/fr/", external: true },
  ],
  emergency: [
    { title: "SOS Violence Conjugale (24/7)", url: "https://sosviolenceconjugale.ca/", external: true, phone: "1-800-363-9010" },
    { title: "Centre de prévention du suicide", url: "https://www.cpsquebec.ca/", external: true, phone: "1-866-277-3553" },
  ]
};

// Update the UrgencySelector component for a more attractive design
const UrgencySelector = ({ options, onSelect }) => (
  <div className="urgency-selector mt-2 mb-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onSelect(option.value)}
          className="urgency-button flex flex-col items-center justify-center p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
          aria-label={option.label}
        >
          <span className="text-2xl mb-2">{option.icon}</span>
          <span className="text-center font-medium text-gray-800">{option.label}</span>
        </button>
      ))}
    </div>
  </div>
);

// Update the FollowUpSelector component for a more attractive design
const FollowUpSelector = ({ question, options, onSelect, t }) => (
  <div className="follow-up-selector mt-2 mb-4">
    <div className="font-medium text-gray-700 mb-2">{question}</div>
    <div className="grid grid-cols-1 gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onSelect(option.value, option.text)}
          className="follow-up-button text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
        >
          <span className="font-medium text-gray-800">{option.text}</span>
          {option.description && (
            <p className="text-sm text-gray-600 mt-1">{option.description}</p>
          )}
        </button>
      ))}
    </div>
  </div>
);

const ClicoSearch = () => {
  const { t, i18n } = useTranslation('clicoSearch');
  const location = useLocation();
  const isStandalonePage = location.pathname === '/clico-search';
  
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState('openai');
  const [chatMessages, setChatMessages] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Set to false by default
  const [showResources, setShowResources] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // New state for handling follow-up questions
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [currentFollowUpIndex, setCurrentFollowUpIndex] = useState(0);
  const [followUpResponses, setFollowUpResponses] = useState([]);
  const [initialQuestion, setInitialQuestion] = useState('');
  const [initialAnalysis, setInitialAnalysis] = useState(null);
  const [showingFollowUp, setShowingFollowUp] = useState(false);
  
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Check if the user is already authenticated in this session
  useEffect(() => {
    const authStatus = sessionStorage.getItem('clicoSearchAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Function to handle successful authentication
  const handleAuthentication = (status) => {
    setIsAuthenticated(status);
    if (status) {
      // Reset chat state after successful login
      const welcomeId = `welcome-${Date.now()}`;
      const welcomeMessage = "Bonjour! Je suis Clicobot, votre assistant d'orientation juridique. Comment puis-je vous aider aujourd'hui?";
      setChatMessages([{ 
        id: welcomeId,
        sender: 'clicobot', 
        text: welcomeMessage, 
        type: 'text', 
        timestamp: new Date()
      }]);
    }
  };
  
  // Add welcome message on first render if authenticated
  useEffect(() => {
    if (chatMessages.length === 0 && isAuthenticated) {
      const welcomeId = `welcome-${Date.now()}`;
      const welcomeMessage = "Bonjour! Je suis Clicobot, votre assistant d'orientation juridique. Comment puis-je vous aider aujourd'hui?";
      setChatMessages([{ 
        id: welcomeId,
        sender: 'clicobot', 
        text: welcomeMessage, 
        type: 'text', 
        timestamp: new Date()
      }]);
    }
    
    if (inputRef.current && isAuthenticated) {
      inputRef.current.focus();
    }
  }, [chatMessages.length, isAuthenticated]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);
  
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (showingFollowUp) {
        handleFollowUpSubmit();
      } else {
        handleSubmit();
      }
    }
  };

  const addMessage = (sender, text, type = 'text') => {
    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setChatMessages(prev => [...prev, { 
      id: messageId, 
      sender, 
      text, 
      type, 
      timestamp: new Date()
    }]);
  };

  // Enhanced handleSubmit for secure server-side API calls
  const handleSubmit = async () => {
    if (!userInput.trim()) return;

    const question = userInput;
    setInitialQuestion(question);
    
    addMessage('user', question);
    setUserInput('');
    setIsLoading(true);
    setError(null);

    try {
      // Construct API URL consistently
      const baseApiUrl = process.env.NODE_ENV === 'production' 
        ? '/api'
        : 'http://localhost:3001/api';
      
      const apiUrl = `${baseApiUrl}/clico-search`;
      
      console.log(`Sending request to API: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question,
          locale: i18n.language,
          modelProvider: selectedModel
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API response error:', response.status, errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || 'Failed to process your question' };
        }
        throw new Error(errorData.message || 'Failed to process your question');
      }
      
      const result = await response.json();
      console.log('API response:', result);
      
      if (result.success && result.data) {
        const data = result.data;
        setInitialAnalysis(data);
        
        // Add detailed explanation if available
        if (data.detailed_explanation) {
          addMessage('clicobot', data.detailed_explanation);
        }
        
        // Add calm message
        if (data.calm_message) {
          addMessage('clicobot', data.calm_message);
        }
        
        // Check for follow-up questions
        if (data.follow_up_questions && data.follow_up_questions.length > 0) {
          setFollowUpQuestions(data.follow_up_questions);
          setFollowUpResponses([]);
          setCurrentFollowUpIndex(0);
          setShowingFollowUp(true);
          
          // Add the first follow-up question as a bot message
          setTimeout(() => {
            addMessage('clicobot', data.follow_up_questions[0]);
          }, 500);
        } else {
          // If no follow-up questions, show resources directly
          if (data.category) {
            setSelectedCategory(data.category);
            setShowResources(true);
          }
          
          // Add disclaimer
          if (data.disclaimer) {
            addMessage('clicobot', data.disclaimer);
          }
        }
      } else {
        setError(result.message || 'Une erreur est survenue lors du traitement de votre question');
      }
    } catch (error) {
      console.error('Error processing question:', error);
      setError(error.message || 'Une erreur est survenue lors du traitement de votre question');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle follow-up question responses
  const handleFollowUpSubmit = async () => {
    if (!userInput.trim()) return;
    
    // Add user response to chat
    addMessage('user', userInput);
    
    // Save the response
    const currentQuestion = followUpQuestions[currentFollowUpIndex];
    const newResponses = [...followUpResponses, {
      question: currentQuestion,
      answer: userInput
    }];
    setFollowUpResponses(newResponses);
    
    // Clear the input
    setUserInput('');
    
    // If there are more follow-up questions, show the next one
    if (currentFollowUpIndex < followUpQuestions.length - 1) {
      setCurrentFollowUpIndex(currentFollowUpIndex + 1);
      const nextQuestion = followUpQuestions[currentFollowUpIndex + 1];
      
      // Add the next question as a bot message
      setTimeout(() => {
        addMessage('clicobot', nextQuestion);
      }, 500);
    } else {
      // If no more follow-up questions, process the responses
      setShowingFollowUp(false);
      setIsLoading(true);
      
      try {
        // Construct API URL consistently 
        const baseApiUrl = process.env.NODE_ENV === 'production' 
          ? '/api'
          : 'http://localhost:3001/api';
          
        const apiUrl = `${baseApiUrl}/clico-search/follow-up`;
          
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            initialQuestion,
            followUpResponses: newResponses,
            initialAnalysis,
            locale: i18n.language,
            modelProvider: selectedModel
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API response error:', response.status, errorText);
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            errorData = { message: errorText || 'Failed to process follow-up responses' };
          }
          throw new Error(errorData.message || 'Failed to process follow-up responses');
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          // Display the refined analysis
          const refinedData = result.data;
          
          // Add detailed explanation if available
          if (refinedData.detailed_explanation) {
            addMessage('clicobot', refinedData.detailed_explanation);
          }
          
          // Add calm message
          if (refinedData.calm_message) {
            addMessage('clicobot', refinedData.calm_message);
          }
          
          // Show resources
          if (refinedData.category) {
            setSelectedCategory(refinedData.category);
            setShowResources(true);
          }
          
          // Add disclaimer
          if (refinedData.disclaimer) {
            addMessage('clicobot', refinedData.disclaimer);
          }
        } else {
          setError(result.message || 'Une erreur est survenue lors du traitement de vos réponses');
        }
      } catch (error) {
        console.error('Error processing follow-up responses:', error);
        setError(error.message || 'Une erreur est survenue lors du traitement de vos réponses');
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // Format text with line breaks and basic formatting
  const formatMessage = (text) => {
    if (!text) return '';
    
    return text
      .split('\n\n')
      .map((paragraph, i) => 
        <p key={i} className={i > 0 ? 'mt-3' : ''}>
          {paragraph}
        </p>
      );
  };

  // Simplified resource rendering
  const renderResources = (category) => {
    if (!category || !BASIC_RESOURCES[category]) return null;
    
    return (
      <div className="resource-category mb-4">
        <div className="font-medium text-gray-700 mb-2">Ressources recommandées:</div>
        <div className="resource-links grid gap-2">
          {BASIC_RESOURCES[category].map((resource, idx) => (
            <div key={idx} className="resource-card bg-white border border-blue-100 rounded-lg p-3 hover:shadow-md transition-shadow">
              {resource.external ? (
                <a 
                  href={resource.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-blue-600 hover:text-blue-800 font-medium"
                >
                  {resource.title} <FaExternalLinkAlt className="inline-block ml-1 text-xs" />
                </a>
              ) : (
                <Link 
                  to={resource.url} 
                  className="block text-blue-600 hover:text-blue-800 font-medium"
                >
                  {resource.title}
                </Link>
              )}
              {resource.phone && (
                <p className="text-sm text-gray-600 mt-1">Téléphone: {resource.phone}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // If the user isn't authenticated, show the authentication form
  if (!isAuthenticated) {
    return (
      <div className={`clico-search-container bg-gray-50 rounded-lg shadow-sm p-4 flex flex-col ${isStandalonePage ? 'mt-20 md:mt-24' : ''}`}>
        <div className="clico-header flex items-center mb-4 pb-3 border-b border-gray-200">
          <div className="clico-logo w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white mr-3">
            <FaLock className="text-xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Clicobot</h2>
            <p className="text-sm text-gray-600">Accès restreint</p>
          </div>
        </div>
        <div className="flex-grow flex items-center justify-center">
          <AuthForm onAuthenticated={handleAuthentication} />
        </div>
      </div>
    );
  }

  return (
    <div className={`clico-search-container bg-gray-50 rounded-lg shadow-sm p-4 h-full flex flex-col ${isStandalonePage ? 'mt-20 md:mt-24' : ''}`}>
      <div className="clico-header flex items-center mb-4 pb-3 border-b border-gray-200">
        <div className="clico-logo w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white mr-3">
          <span className="text-xl font-bold">C</span>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">Clicobot</h2>
          <p className="text-sm text-gray-600">Assistance juridique</p>
        </div>
        <div className="ml-auto flex items-center">
          <div className="mr-4">
            <label htmlFor="modelProviderSelect" className="mr-2 text-sm font-medium text-gray-700">Modèle IA:</label>
            <select 
              id="modelProviderSelect"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="p-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="openai">OpenAI (GPT-4o mini) - Recommandé FR</option>
              <option value="gemini">Google (Gemini) - Apprenti FR</option>
            </select>
            {selectedModel === 'gemini' && (
              <div className="text-xs text-amber-600 mt-1">
                <FaExclamationTriangle className="inline-block mr-1" />
                Support limité pour les accents français
              </div>
            )}
          </div>
          <button 
            className="emergency-button bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-full text-sm font-medium flex items-center"
            onClick={() => {
              setSelectedCategory('emergency');
              setShowResources(true);
              addMessage('clicobot', "Si vous êtes en situation d'urgence, voici des ressources qui peuvent vous aider immédiatement:");
            }}
          >
            <FaExclamationTriangle className="mr-1" />
            Urgence
          </button>
          
          <button 
            className="ml-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center"
            onClick={() => {
              sessionStorage.removeItem('clicoSearchAuthenticated');
              setIsAuthenticated(false);
            }}
          >
            <FaLock className="mr-1" />
            Déconnexion
          </button>
        </div>
      </div>
      
      <div 
        className="chat-messages overflow-y-auto flex-grow mb-4 p-2"
        ref={chatContainerRef}
      >
        {chatMessages.map((msg) => (
          <div 
            key={msg.id} 
            className={`chat-message mb-4 ${msg.sender === 'user' ? 'user-message text-right' : 'clicobot-message'}`}
          >
            <div className="message-bubble inline-block max-w-[85%] rounded-lg px-4 py-2 shadow-sm" 
                 style={{ 
                   backgroundColor: msg.sender === 'user' ? '#DCF8C6' : 'white',
                   borderRadius: msg.sender === 'user' ? '1rem 0 1rem 1rem' : '0 1rem 1rem 1rem' 
                 }}>
              {msg.sender === 'clicobot' && (
                <div className="text-xs text-gray-500 mb-1">Clicobot</div>
              )}
              <div className="mb-1">{formatMessage(msg.text)}</div>
            </div>
          </div>
        ))}
        
        {showResources && selectedCategory && (
          <div className="chat-message mb-4 clicobot-message">
            <div className="message-bubble inline-block max-w-[85%] rounded-lg px-4 py-2 shadow-sm bg-white" 
                 style={{ borderRadius: '0 1rem 1rem 1rem' }}>
              <div className="text-xs text-gray-500 mb-1">Clicobot</div>
              {renderResources(selectedCategory)}
            </div>
          </div>
        )}
        
        {isLoading && (
          <div className="clicobot-typing mb-4">
            <div className="message-bubble inline-block rounded-lg px-4 py-2 bg-white shadow-sm">
              <div className="text-xs text-gray-500 mb-1">Clicobot</div>
              <div className="typing-indicator flex space-x-1">
                <div className="typing-dot w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="typing-dot w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                <div className="typing-dot w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <div className="error-message mb-3 p-3 bg-red-100 text-red-700 rounded-lg flex items-center">
          <FaExclamationCircle className="mr-2" /> {error}
          <button 
            onClick={() => setError(null)} 
            className="ml-auto text-red-700 hover:text-red-900"
            aria-label="Dismiss error"
          >
            <FaRedo />
          </button>
        </div>
      )}
      
      <form
        onSubmit={(e) => { 
          e.preventDefault(); 
          if (showingFollowUp) {
            handleFollowUpSubmit();
          } else {
            handleSubmit();
          }
        }}
        className="chat-input-area flex items-end"
      >
        <div className="relative flex-grow">
          <textarea
            ref={inputRef}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={showingFollowUp ? "Répondez à la question..." : "Posez votre question juridique ici..."}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows="2"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="absolute right-2 bottom-2 bg-blue-600 text-white rounded-full p-2 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-700 disabled:opacity-50"
            disabled={!userInput.trim() || isLoading}
          >
            {isLoading ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClicoSearch;
